
import React, { useState, useEffect } from "react";
import { Bell, BellDot, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import NotificationItem from "./NotificationItem";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Notification } from "@/types/notification";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

const NotificationsDropdown: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();
  const { toast } = useToast();

  // Function to fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) {
        console.log("Error fetching notifications:", error.message);
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      // Map the data to match the Notification interface
      const mappedNotifications = data.map(notification => ({
        id: notification.id,
        content: notification.content,
        isRead: notification.is_read,
        time: formatTime(notification.created_at),
        created_at: notification.created_at
      }));
      
      setNotifications(mappedNotifications);
      setUnreadCount(mappedNotifications.filter(n => !n.isRead).length);
      
    } catch (error: any) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Format time for display
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Mark notification as read
  const handleNotificationClick = async (id: string) => {
    // Find and update the notification in the local state
    const updatedNotifications = notifications.map(notification => {
      if (notification.id === id && !notification.isRead) {
        return { ...notification, isRead: true };
      }
      return notification;
    });
    
    setNotifications(updatedNotifications);
    setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    
    // Update in the database
    try {
      await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('id', id);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    if (!user) return;
    
    try {
      setNotifications([]);
      setUnreadCount(0);
      
      await supabase
        .from('user_notifications')
        .delete()
        .eq('user_id', user.id);
        
      toast({
        title: "Notifications Cleared",
        description: "All notifications have been cleared"
      });
    } catch (error) {
      console.error("Error clearing notifications:", error);
      toast({
        title: "Error",
        description: "Could not clear notifications",
        variant: "destructive"
      });
    }
  };

  // Fetch notifications on component mount and when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);

  // Set up real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const newNotification = {
          id: payload.new.id,
          content: payload.new.content,
          isRead: payload.new.is_read,
          time: formatTime(payload.new.created_at),
          created_at: payload.new.created_at
        };
        
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user]);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative rounded-full bg-muted hover:bg-primary/10 border-primary/20"
          aria-label="Notifications"
        >
          {unreadCount > 0 ? (
            <BellDot size={20} className="text-primary" />
          ) : (
            <Bell size={20} className="text-muted-foreground" />
          )}
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center text-[10px] font-bold animate-pulse"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-card shadow-xl rounded-lg border-0" align="end">
        <div className="border-b py-3 px-4 flex justify-between items-center bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
          <h4 className="font-semibold text-foreground">Notifications</h4>
          {notifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllNotifications} 
              className="h-8 text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Clear All
            </Button>
          )}
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              <div className="animate-pulse flex justify-center py-8">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="divide-y divide-muted/20">
              {notifications.map((notification) => (
                <NotificationItem 
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <Bell size={24} className="mx-auto mb-2 text-muted-foreground/50" />
              <p>No notifications to display</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsDropdown;
